import axios, { AxiosInstance } from 'axios';
import { Task } from '../types/Task';
import { loginRequest, msalInstance } from '../config/MsalConfig';
import { InteractionRequiredAuthError } from "@azure/msal-browser";

class ApiService {
  private apiClient: AxiosInstance;
  private hostURL: string;

  constructor(baseURL, hostURL) {
    this.hostURL = hostURL;
    this.apiClient = axios.create({
      baseURL: baseURL,
      timeout: 10000,
      // Do NOT set a default Content-Type here.
      // Axios sets 'application/json' automatically for plain-object bodies via transformRequest.
      // A hardcoded default would override the browser's 'multipart/form-data; boundary=...' for
      // FormData requests (e.g. file import), causing FastAPI to return 422 Unprocessable Entity.
    });

    this.apiClient.interceptors.request.use(async (config) => {
      const account = msalInstance.getAllAccounts()[0];
      if (account) {
        try {
          // acquireTokenSilent returns a fresh token (from cache or via refresh).
          // Using the return value directly avoids any localStorage timing issues.
          const token = await this.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            // acquireTokenRedirect was already triggered inside getToken().
            // Reject the queued request cleanly — the browser will navigate to login.
            return Promise.reject(err);
          }
          // Non-interactive errors (network, config): log and send the request unauthenticated.
          console.error('Token acquisition failed in request interceptor:', err);
        }
      }
      return config;
    }, (error) => {
      return Promise.reject(error);
    });

    this.apiClient.interceptors.response.use((response) => {
      return response;
    }, async (error) => {
      const originalRequest = error.config as (typeof error.config & { _retry?: boolean });
      // On 401, force-refresh the token once and retry
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
          try {
            const response = await msalInstance.acquireTokenSilent({
              account,
              scopes: loginRequest.scopes,
              forceRefresh: true,
            });
            const newToken = response.accessToken;
            // Set the token only on the retried request, not on shared defaults.
            // Mutating defaults would affect all concurrent requests and is architecturally wrong.
            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            }
            return this.apiClient(originalRequest);
          } catch (refreshError) {
            console.error('Token refresh on 401 failed:', refreshError);
            return Promise.reject(refreshError);
          }
        }
      }
      return Promise.reject(error);
    });
  }

  async getTasks() {
    const response = await this.apiClient.get<Task[]>('/tasks');
    return response.data;
  }

  async createTask(taskData: { name: string; description: string; proprity: string, category: string, targetDateRaw: Date, assigned: string }) {
    taskData["targetDate"] = new Date(taskData.targetDateRaw);
    const response = await this.apiClient.post('/tasks', taskData);
    return response.data;
  }

  async getTaskById(id: string) {
    const response = await this.apiClient.get(`/tasks/${id}`);
    return response.data;
  }

  async updateTask(id: string, taskData: { name: string; description: string }) {
    const response = await this.apiClient.put(`/tasks/${id}`, taskData);
    return response.data;
  }

  async deleteTask(id: string) {
    const response = await this.apiClient.delete(`/tasks/${id}`);
    return response.data;
  }

  async getToken(): Promise<string | null> {
    const account = msalInstance.getAllAccounts()[0];
    if (!account) return null;
    try {
      const response = await msalInstance.acquireTokenSilent({
        account: account,
        scopes: loginRequest.scopes,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // acquireTokenRedirect navigates away — throw so callers know no token is available
        // and don't proceed with unauthenticated requests.
        // acquireTokenRedirect navigates the page; fire-and-forget is intentional here.
        void msalInstance.acquireTokenRedirect({
          scopes: loginRequest.scopes,
        });
        throw error;
      } else {
        console.error('Silent token acquisition failed:', error);
        throw error;
      }
    }
  }

  getApiClient(): AxiosInstance {
    return this.apiClient;
  }

  getHostURL(): string {
    return this.hostURL;
  }

  reconfigure(backendHost: string, hostURL: string): void {
    this.hostURL = hostURL;
    this.apiClient.defaults.baseURL = backendHost;
  }
}

// Initialise synchronously with defaults — no top-level await
const DEFAULT_BACKEND = 'https://dc-be-team-spa.sdc.apps.issworld-test.com';
const DEFAULT_HOST = 'https://dc-fe-team-spa.sdc.apps.issworld-test.com';

const apiService = new ApiService(DEFAULT_BACKEND, DEFAULT_HOST);

// Exposed so index.tsx can render a visible banner when the backend URL was not substituted.
export let backendHostWarning: string | null = null;

// Resolves after config.json has been applied (or on failure).
// Await this before making any requests to guarantee the correct base URL is used.
export const apiServiceReady: Promise<void> = axios.get('/config.json')
  .then((response) => {
    const { BACKEND_HOST, HOST } = response.data;
    // Ignore unsubstituted deploy-time placeholders (e.g. "__BACKEND_HOST__")
    const isReal = (v: unknown) => typeof v === 'string' && v.startsWith('http');
    if (!isReal(BACKEND_HOST)) {
      backendHostWarning =
        'Backend URL (BACKEND_HOST) was not substituted in config.json — ' +
        'API requests will fall back to localhost. Check your CI/CD substitution step.';
      console.error(backendHostWarning);
    }
    if (isReal(BACKEND_HOST) || isReal(HOST)) {
      apiService.reconfigure(
        isReal(BACKEND_HOST) ? BACKEND_HOST : DEFAULT_BACKEND,
        isReal(HOST) ? HOST : DEFAULT_HOST,
      );
    }
  })
  .catch(() => {
    // config.json not available — keep defaults and surface a clear warning.
    backendHostWarning =
      'Runtime config.json not found — API uses fallback BACKEND_HOST: ' +
      `${DEFAULT_BACKEND}.`;
    console.warn(backendHostWarning);
  });

export default apiService;